import { Container, Label } from "./FormInput";
import styled from "styled-components";
import { FaTimesCircle } from "react-icons/fa";
import { uniq } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { transparentize } from "polished";

const InputContainer = styled.div`
  border: 1px solid rgba(128, 128, 128, 0.8);
  border-radius: 3px;
  background-color: ${(props) => props.theme.colors.background};
  width: 100%;
  display: block;
  color: ${(props) => props.theme.colors.body};
  display: flex;
  flex-wrap: wrap;
  padding: 4px;
  padding-bottom: 0px;
  cursor: text;

  &:focus-within,
  &:active {
    outline: 0;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: inset 0 0 3px
      ${(props) => transparentize(0.8, props.theme.colors.primary)};
  }

  & input {
    padding: 3px;
    margin-bottom: 4px;
    min-width: 50px;
    flex: 1;
    border: 0;
    background: transparent;
    color: ${(props) => props.theme.colors.body};

    &:active,
    &:focus {
      outline: 0;
    }
    &::placeholder {
      font-size: 12px;
      color: #aaa;
    }
  }
`;

const TagLabel = styled.span`
  background-color: ${(props) => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  padding: 3px 6px;
  line-height: 1;
  font-size: 14px;
  margin: 3px;
  margin-bottom: 4px;
  margin-top: 0;
  margin-right: 4px;
  cursor: default;

  & + &,
  &:first-child {
    margin-left: 0;
  }
`;

const DelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px;
  background: none;
  border: 0;
  color: white;
  opacity: 0.8;
  cursor: pointer;
  margin-left: 6px;
  margin-right: 3px;
  &:hover {
    opacity: 1;
  }
`;

export type TagInputProps = {
  value: string[];
  onChange: (val: string[]) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

export function TagInput({ value, onChange, onFocus, onBlur }: TagInputProps) {
  const [partial, setPartial] = useState("");
  const inputRef = useRef<HTMLInputElement>(null!);

  const addTag = useCallback(() => {
    const p = partial;
    setPartial("");
    if (p.trim() !== "") {
      onChange(
        uniq(value.concat([partial.trim()]).map((x) => x.toLowerCase()))
      );
    }
    onBlur && onBlur();
  }, [onBlur, partial, value, onChange]);

  useEffect(() => {
    if (partial.endsWith(" ")) {
      addTag();
    }
  }, [partial, addTag]);

  return (
    <Container>
      <Label htmlFor="tags">Tags</Label>
      <InputContainer onClick={() => inputRef.current.focus()}>
        {value.map((x) => (
          <TagLabel>
            {x}
            <DelButton onClick={() => onChange(value.filter((v) => v !== x))}>
              <FaTimesCircle />
            </DelButton>
          </TagLabel>
        ))}
        <input
          id="tags"
          ref={inputRef}
          value={partial}
          onFocus={onFocus}
          onBlur={addTag}
          onChange={(ev) => {
            setPartial(ev.target.value);
          }}
          onKeyDown={(ev) => {
            if (ev.key === " " || ev.key === "Enter" || ev.key === "Return") {
              ev.preventDefault();
              addTag();
            } else if (ev.key === "Backspace" && partial.length === 0) {
              onChange(value.slice(0, -1));
            }
          }}
        />
      </InputContainer>
    </Container>
  );
}
